import { Box, Button, CircularProgress, Container, CssBaseline, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { gateway } from "../../../gateway";
import { ISignupRole } from "../../../utils";
import { SnackbarAlert } from "../../common/SnackbarAlert";
import { GoogleButtonAuthenticate } from "../../common/google/GoogleButtonAuthenticate";
import { useSnackbar } from "../../common/snackbar/useSnackbar";
import { colors } from "../../ui/colors";
import { AccountVerification } from "../accountVerify/AccountVerification";
import { useInvitateSignUp } from "./InvitateIndividual/hooks/useInvitateSingUp";
import { AccountInformationForm } from "./components/AccountInformationForm";
import { PersonalInformationForm } from "./components/PersonalInformationForm";
import { ReviewInformationForm } from "./components/ReviewInformationForm";
import { SideStepNavigation } from "./components/SideStepNavigation";
import { signUpIndividualSteps } from "./constants/individual.constants";

export const SignUpIndividual = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wasCreated, setWasCreated] = useState(false);
  const [hasGoogleData, setHasGoogleData] = useState(false);
  const [hasPendingActivation, setHasPendingActivation] = useState(false);

  const handleEditStepper = (step) => {
    setActiveStep(step);
  };

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const invitationUid = queryParams.get("invitationUid");

  const { userInvitationInfo } = useInvitateSignUp(invitationUid);
  const [hasInitializedForm, setHasInitializedForm] = useState(false);

  const location = useLocation();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      countryCode: "US",
      dialCode: "+1",
      firstName: "",
      lastName: "",
      alias: "",
      email: "",
    },
  });

  // Inicializar el formulario si viene token
  useEffect(() => {
    if (userInvitationInfo && !hasInitializedForm) {
      reset({
        countryCode: userInvitationInfo.countryCode || "US",
        dialCode: userInvitationInfo.dialCode || "+1",
        firstName: userInvitationInfo.firstName || "",
        lastName: userInvitationInfo.lastName || "",
        alias: userInvitationInfo.alias || "",
        email: userInvitationInfo.email || "",
        phone: userInvitationInfo.phone || "",
        mailingAddress: userInvitationInfo.address || "",
      });

      showSnackbar("We've pre-filled your information from the invitation sent by your company", "info");
      setHasInitializedForm(true);
    }
  }, [userInvitationInfo, reset, hasInitializedForm, showSnackbar]);

  const handleGoogleSingUpData = (userInfo) => {
    setValue("firstName", userInfo.firstName || "");
    setValue("lastName", userInfo.lastName || "");
    setValue("email", userInfo.email || "");
  };

  useEffect(() => {
    if (location.state?.userInfo) {
      handleGoogleSingUpData(location.state.userInfo);
      showSnackbar("Almost done! Please complete your profile.", "info");
      setHasGoogleData(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    try {
      const rawValue = localStorage.getItem("pendingActivationAccount");
      if (!rawValue) return;

      const parsedValue = JSON.parse(rawValue);

      if (parsedValue?.uid && parsedValue?.activationToken) {
        setHasPendingActivation(true);
      }
    } catch {
      setHasPendingActivation(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 200);
    return () => clearTimeout(timeout);
  }, []);

  const handleNext = async () => {
    let fieldsToValidate = [];
    if (activeStep === 0) {
      fieldsToValidate = ["firstName", "lastName", "email", "password", "confirmPassword"];
    } else if (activeStep === 1) {
      fieldsToValidate = ["age", "mailingAddress", "workAs", "individualIndustry", "phone"];
    }

    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      if (activeStep === signUpIndividualSteps.length - 1) {
        handleFormSubmit(getValues());
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFormSubmit = async (data) => {
    setIsLoading(true);

    try {
      const url = `/users/signup`;

      const { confirmPassword, ...payload } = data;

      const response = await gateway.post(url, {
        ...payload,
        ...(invitationUid ? { invitationUid } : {}),
        email: data.email.toLowerCase().trim(),
        accountType: ISignupRole.EMPLOYEE,
      });

      if (response.status === 201 || response.data?.message === "User needs to be activated") {
        showSnackbar("Individual registered successfully!", "success");

        const pendingActivationAccountIndividual = {
          uid: response?.data?.data?.user?.uid,
          activationToken: response?.data?.data?.activationToken,
        };

        localStorage.setItem("pendingActivationAccount", JSON.stringify(pendingActivationAccountIndividual));
        setWasCreated(true);
      }

      if (response.status === 201 && response.data?.message === "Invited user was created") {
        showSnackbar("Individual registered successfully!", "success");
        sessionStorage.setItem("token", response.data?.data?.auth?.token);
        sessionStorage.setItem("refresh_token", response.data?.data?.auth?.refresh_token?.token);

        navigate("/");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "An error occurred during registration";
      showSnackbar(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <AccountInformationForm control={control} errors={errors} />;
      case 1:
        return <PersonalInformationForm setValue={setValue} control={control} errors={errors} />;
      case 2:
        return <ReviewInformationForm formData={getValues()} handleEditStepper={handleEditStepper} />;
      default:
        return "Unknown step";
    }
  };

  if (wasCreated || hasPendingActivation) {
    return <AccountVerification />;
  }

  return (
    <Container component="main" maxWidth="lg">
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.5s",
          py: 4,
        }}
      >
        <Box
          sx={{
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            p: { xs: 2, sm: 3, md: 4 },
            width: "100%",
            maxWidth: "1200px",
            minHeight: { xs: "auto", md: "600px" },
            display: "flex",
            flexDirection: "column",
            borderRadius: "16px",
          }}
        >
          {/* Header */}
          <Typography
            component="h1"
            variant="h1"
            sx={{
              fontWeight: 1000,
              color: colors.gray.base,
              mb: 1,
              fontSize: { xs: "24px", sm: "32px", md: "48px" },
              letterSpacing: "1px",
              textAlign: "center",
            }}
          >
            SIGN UP
          </Typography>

          {/* Decorative bar */}
          <Box
            sx={{
              height: { xs: 2, sm: 3, md: 4 },
              width: { xs: "50%", sm: "180px", md: "220px" },
              borderRadius: "999px",
              mb: { xs: 3, md: 4 },
              background: `linear-gradient(90deg, ${colors.orange[300]}, ${colors.yellow[300]})`,
              mx: "auto",
            }}
          />

          {/* Content */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              width: "100%",
            }}
          >
            <SideStepNavigation activeStep={activeStep} />

            <Box
              sx={{
                flex: 1,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
              }}
            >
              <form onSubmit={handleSubmit(handleFormSubmit)}>{getStepContent(activeStep)}</form>

              {activeStep === 0 && !hasGoogleData && (
                <Box
                  sx={{
                    mt: 4,
                    pt: 3,
                    borderTop: `2px solid ${colors.gray[200]}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      color: colors.gray[400],
                      mb: 2,
                    }}
                  >
                    Or sign up with
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <GoogleButtonAuthenticate handleGoogleSingUpData={handleGoogleSingUpData} />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 3,
              gap: 1.5,
            }}
          >
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              size="small"
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: "6px",
                backgroundColor: colors.gray[200],
                color: colors.gray.base,
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                "&:hover": { backgroundColor: colors.gray[300] },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isLoading}
              size="small"
              sx={{
                px: 2.5,
                py: 0.5,
                borderRadius: "6px",
                backgroundColor: colors.orange.base,
                color: "white",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: colors.orange[600],
                  boxShadow: "none",
                },
              }}
            >
              {isLoading ? <CircularProgress size={18} color="inherit" /> : activeStep === signUpIndividualSteps.length - 1 ? "Submit" : "Next"}
            </Button>
          </Box>
        </Box>
      </Box>

      <SnackbarAlert openState={snackbar.open} setOpenState={closeSnackbar} severity={snackbar.severity} message={snackbar.message} />
    </Container>
  );
};
